import { DateTime } from 'luxon';
import { useCallback } from 'react';

import { useCanCreateTripInThePast } from './useCanCreateTripInThePast';
import {
  validateTripDate,
  validateTripMaxFutureDate,
} from './validateTripDate';
import { validateTripDateOnEditingTripInThePast } from './validateTripDateOnEditingTripInThePast';
import { FormField } from 'src/common/FormField';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { useIsSelectedTripInThePast } from 'src/features/add-trip/useIsSelectedTripInThePast';

export function TripDateContextField({ fieldPath }: { fieldPath: string }) {
  const isSelectedTripInThePast = useIsSelectedTripInThePast();
  const canCreateTripInThePast = useCanCreateTripInThePast();
  const validate = useTripDateValidation();

  return (
    <FormFieldController
      name={`${fieldPath}.date`}
      rules={{
        validate,
      }}
    >
      {({ field, fieldState }) => {
        return (
          <FormField
            label="Trip Date *"
            error={fieldState.error?.message?.toString()}
            inputProps={{
              id: `${fieldPath}.date`,
              ...field,
              onChange: field.onChange,
              min: canCreateTripInThePast
                ? undefined
                : DateTime.local().toISODate(),
              disabled: isSelectedTripInThePast,
            }}
            {...field}
            type="date"
          />
        );
      }}
    </FormFieldController>
  );
}

function useTripDateValidation() {
  const isSelectedTripInThePast = useIsSelectedTripInThePast();
  const canCreateTripInThePast = useCanCreateTripInThePast();
  const validate = useCallback(
    (value: any) => {
      if (isSelectedTripInThePast) {
        return validateTripDateOnEditingTripInThePast(value);
      }

      const validateFutureDate = validateTripMaxFutureDate(value);

      if (typeof validateFutureDate === 'string') {
        return validateFutureDate;
      }

      if (canCreateTripInThePast) {
        return !!value || 'Trip Date is required';
      }
      return validateTripDate(value);
    },
    [canCreateTripInThePast, isSelectedTripInThePast]
  );

  return validate;
}
